import React, { useState } from "react";
import {
  Card,
  Tag,
  Progress,
  Button,
  Modal,
  List,
  Divider,
  Typography,
  Space,
  Flex,
  theme,
} from "antd";
import {
  CirclePlay,
  CircleCheck,
  CircleMinus,
  X,
  Book,
  FileText,
  Clock,
  Lock,
} from "lucide-react";
import Icon from "./Icon";
import { useNavigate } from "react-router-dom";
import { useUserProgress } from "../hooks/useUserProgress";
import { getSafeImageUrl } from "../utils/imageHelper";
import { useThemeMode } from "../theme/ThemeProvider";
import { alpha } from "../utils/color";

const { Text, Title } = Typography;
const COURSE_FALLBACK_IMAGE = "/no-image.jpg";

const clampProgress = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.min(100, Math.max(0, numericValue));
};

const ModuleContainerCard = ({ container, onClick }) => {
  const navigate = useNavigate();
  const { userProgress: moduleProgress } = useUserProgress();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { token } = theme.useToken();
  const { mode } = useThemeMode();

  // Add safety checks for moduleProgress
  const safeModuleProgress = moduleProgress || {};

  // Calculate progress statistics
  const hasModules = container.modules && container.modules.length > 0;
  const completedModules = hasModules ? container.getCompletedModulesCount(safeModuleProgress) : 0;
  const overallProgress = clampProgress(hasModules ? container.getOverallProgress(safeModuleProgress) : 0);
  const roundedOverallProgress = Math.round(overallProgress);
  const isCompleted = hasModules && container.isCompleted(safeModuleProgress);
  const availableModules = hasModules ? container.modules.filter((module) => !module.isLocked) : [];
  const totalQuizQuestions = hasModules ? container.getTotalQuizQuestions() : 0;
  const totalSteps = hasModules ? container.getTotalSteps() : 0;

  const handleCardClick = () => {
    // Don't open dialog if container is locked or coming soon
    if (container.isLocked || container.isComingSoon) {
      return;
    }
    setDialogOpen(true);
  };

  const handleModuleClick = (module, index) => {
    // Check if user is logged in
    if (!moduleProgress) {
      setDialogOpen(false);
      // Could redirect to login page or show message
      return;
    }

    // For modules in container, check if previous module is completed
    if (index > 0) {
      const previousModule = container.modules[index - 1];
      if (previousModule) {
        const previousProgress = moduleProgress[previousModule.id];
        const isPreviousCompleted = previousProgress?.completed === true;

        if (!isPreviousCompleted) {
          setDialogOpen(false);
          // Show error message or prevent navigation
          return;
        }
      }
    }

    setDialogOpen(false);
    navigate(`/module/${module.id}`);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const getModuleStatus = (module, index) => {
    // Add null checking for moduleProgress
    if (!moduleProgress || !module || !module.id) {
      return "not-started";
    }

    // For modules in container, check sequential unlocking
    if (index > 0) {
      const previousModule = container.modules[index - 1];
      if (previousModule) {
        const previousProgress = moduleProgress[previousModule.id];
        const isPreviousCompleted = previousProgress?.completed === true;

        if (!isPreviousCompleted) {
          return "locked";
        }
      }
    }

    const progress = moduleProgress[module.id];

    const currentVersion = progress?.version ?? 1;
    if (currentVersion < module.version && progress) return "updated";

    // Check if module is completed
    if (progress?.completed) {
      return "completed";
    }

    // Check if module is in progress (has currentStepId but not completed)
    const hasStarted = progress?.currentStepId || progress?.stepId;
    if (hasStarted && hasStarted !== "completed") {
      return "in-progress";
    }

    // Default to not-started
    return "not-started";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <Icon as={CircleCheck} strokeWidth={2.5} style={{ color: token.colorText }} />;
      case "in-progress":
        return <Icon as={CirclePlay} style={{ color: token.colorText }} />;
      case "locked":
        return <Icon as={Lock} style={{ color: token.colorTextDisabled }} />;
      default:
        return <Icon as={CircleMinus} style={{ color: token.colorTextDisabled }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "processing";
      case "in-progress":
        return "processing";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "completed":
        return "Completed";
      case "in-progress":
        return "In Progress";
      case "updated":
        return "updated";
      case "locked":
        return "Locked";
      default:
        return "Start";
    }
  };

  const isDisabled = container.isLocked || container.isComingSoon;

  return (
    <>
      <style>{`
        .course-card {
          height: 100%;
          display: flex;
          flex-direction: column;
          border-radius: ${token.borderRadius}px;
          overflow: hidden;
          border: 1px solid ${token.colorBorderSecondary};
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: ${mode === "dark" ? token.colorBgContainer : "#fff"};
          font-family: var(--font-untitled-sans);
        }
        .course-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 30px ${alpha(token.colorText, 0.1)};
        }
        .course-card-cover {
          position: relative;
          height: 220px;
          overflow: hidden;
        }
        .course-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .course-card:hover .course-card-image {
          transform: scale(1.08);
        }
        .course-card-tag {
          position: absolute;
          top: 16px;
          left: 16px;
          backdrop-filter: blur(4px);
          padding: 4px 12px;
          border-radius: ${token.borderRadius / 2}px;
          font-weight: 400;
          font-size: 12px;
          z-index: 2;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          font-family: var(--font-untitled-sans);
        }
        .course-card-content {
          padding: 24px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .course-card-footer {
          padding: 16px 24px;
          border-top: 1px solid ${token.colorBorderSecondary};
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: ${mode === "dark" ? "rgba(255, 255, 255, 0.01)" : "rgba(0, 0, 0, 0.01)"};
        }
      `}</style>

      <div className="course-card" onClick={handleCardClick} style={{ cursor: isDisabled ? "not-allowed" : "pointer", opacity: isDisabled ? 0.7 : 1 }}>
        <div className="course-card-cover">
          {container.isUpdate && (
            <div
              className="course-card-tag"
              style={{
                background: "#111827",
                color: "#fff"
              }}
            >
              updated
            </div>
          )}
          <img 
            src={getSafeImageUrl(container.thumbnail) || COURSE_FALLBACK_IMAGE}
            alt={container.name} 
            className="course-card-image"
            style={{ filter: isDisabled ? "grayscale(1)" : "none" }}
            onError={(event) => {
              if (!event.currentTarget.src.endsWith(COURSE_FALLBACK_IMAGE)) {
                event.currentTarget.src = COURSE_FALLBACK_IMAGE;
              }
            }}
          />
          {isDisabled && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3, backdropFilter: "blur(0.5px)" }}>
              {container.isComingSoon ? (
                <div style={{ textAlign: 'center', padding: '0 20px' }}>
                  <Icon as={Clock} size={32} style={{ color: "#fff", margin: "0 auto 12px", display: "block" }} />
                  <Text style={{ color: "#fff", fontWeight: 400, fontSize: 14, textTransform: 'uppercase', letterSpacing: 2, fontFamily: 'var(--font-sf-pro)' }}>Coming Soon</Text>
                </div>
              ) : (
                <Icon as={Lock} size={40} style={{ color: "#fff" }} />
              )}
            </div>
          )}
        </div>

        <div className="course-card-content">
          <Title level={4} style={{ 
            margin: "0 0 12px 0", 
            fontSize: "1.25rem",
          }}>
            {container.name}
          </Title>
          <Text type="secondary" style={{ 
            display: "-webkit-box", 
            WebkitLineClamp: 3, 
            WebkitBoxOrient: "vertical", 
            overflow: "hidden",
            lineHeight: "1.6",
            fontSize: "14px",
            minHeight: "68px",
            fontWeight: 400
          }}>
            {container.description}
          </Text>

          {/* Tags list back again */}
          <div style={{ marginTop: 12 }}>
            <Flex wrap="wrap" gap={6}>
              {container.tags.slice(0, 3).map((tag, index) => (
                <Tag
                  key={index}
                  bordered={false}
                  style={{
                    backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
                    fontSize: "11px",
                    fontWeight: 500,
                    margin: 0,
                    borderRadius: "4px",
                    color: token.colorTextSecondary
                  }}
                >
                  {tag}
                </Tag>
              ))}
            </Flex>
          </div>

          {/* Statistics below description */}
          <div style={{ marginTop: "auto", paddingTop: 16 }}>
            <Flex gap={12} wrap="wrap">
               <Flex align="center" gap={4}>
                  <Icon as={Book} size={14} style={{ color: token.colorText }} />
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
                    {availableModules.length} Modules
                  </Text>
               </Flex>
               <Flex align="center" gap={4}>
                  <Icon as={FileText} size={14} style={{ color: token.colorText }} />
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
                    {totalQuizQuestions} Quizzes
                  </Text>
               </Flex>
            </Flex>
          </div>
        </div>

        <div className="course-card-footer">
          <div style={{ flex: 1, marginRight: 16 }}>
            {overallProgress > 0 && (
              <Progress 
                percent={roundedOverallProgress}
                size="small" 
                showInfo={false} 
                strokeColor={token.colorText}
                trailColor={alpha(token.colorText, 0.06)}
                strokeWidth={8}
              />
            )}
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 700 }}>
              {isCompleted ? "COMPLETED" : overallProgress > 0 ? `${roundedOverallProgress}% DONE` : ""}
            </Text>
          </div>
          <Button 
            type="primary" 
            disabled={isDisabled}
            style={{ 
              borderRadius: token.borderRadius / 2,
              fontWeight: 400,
              background: isCompleted ? token.colorText : token.colorPrimary,
              color: token.colorTextLightSolid,
              border: "none",
              padding: "0 20px"
            }}
          >
            {container.isComingSoon ? "Coming Soon" : (isCompleted ? "Review" : overallProgress > 0 ? "Continue" : "Start Learning")}
          </Button>
        </div>
      </div>

      {/* Module List Dialog - Only show if not locked and not coming soon */}
      {!isDisabled && (
        <Modal
          open={dialogOpen}
          onCancel={handleDialogClose}
          width={800}
          footer={
            <Button onClick={handleDialogClose} type="default">
              Close
            </Button>
          }
          title={
            <div style={{ paddingRight: 32 }}>
              <Title level={4} style={{ 
                margin: 0, 
                fontFamily: "var(--font-sf-pro)", 
                fontWeight: 400, 
                textTransform: "uppercase",
                letterSpacing: "0.2em"
              }}>
                {container.name}
              </Title>
              <Text type="secondary" style={{ 
                marginTop: 8, 
                display: "block", 
                fontFamily: "var(--font-untitled-sans)",
                fontWeight: 400,
                fontSize: 14
              }}>
                {completedModules}/{availableModules.length} modules completed
                {" "}&#8226;{" "}{roundedOverallProgress}% progress
              </Text>
              <Progress
                percent={roundedOverallProgress}
                strokeColor={isCompleted ? token.colorText : token.colorPrimary}
                trailColor={alpha(isCompleted ? token.colorText : token.colorPrimary, 0.06)}
                strokeWidth={6}
                showInfo={false}
                style={{ marginTop: 16 }}
              />
            </div>
          }
          styles={{
            body: { padding: "12px 0 24px 0" },
          }}
          closeIcon={<Icon as={X} size={16} />}
          style={{ borderRadius: token.borderRadius * 1.5 }}
          footer={null}
        >
          <Divider style={{ margin: "12px 0 0 0", opacity: 0.6 }} />
          <List
            dataSource={availableModules}
            split={false}
            renderItem={(module, index) => {
              const status = getModuleStatus(module, index);
              const isLastItem = index === availableModules.length - 1;
              const isLocked = status === "locked";

              return (
                <React.Fragment key={module.id}>
                  <List.Item
                    onClick={() => !isLocked && handleModuleClick(module, index)}
                    style={{
                      padding: "20px 24px",
                      cursor: isLocked ? "not-allowed" : "pointer",
                      opacity: isLocked ? 0.6 : 1,
                      transition: "all 0.2s ease",
                      borderBottom: `1px solid ${alpha(token.colorBorder, 0.4)}`,
                      fontFamily: "var(--font-untitled-sans)"
                    }}
                    onMouseEnter={(e) => {
                      if (!isLocked) {
                        e.currentTarget.style.backgroundColor = alpha(token.colorPrimary, 0.02);
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <Flex align="flex-start" gap={16} style={{ width: "100%" }}>
                      <div style={{ paddingTop: 2 }}>
                        {getStatusIcon(status)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Flex align="center" gap={12} wrap="wrap">
                          <Text
                            style={{
                              fontWeight: 400,
                              fontSize: 16,
                              color: isLocked ? token.colorTextDisabled : token.colorText,
                            }}
                          >
                            Module : {module.name}
                          </Text>
                          {module.tags && module.tags.slice(0, 3).map((tag, idx) => (
                            <Tag
                              key={idx}
                              bordered={false}
                              style={{ 
                                background: alpha(token.colorText, 0.08),
                                color: token.colorText,
                                fontSize: 11,
                                borderRadius: token.borderRadius / 4,
                                fontWeight: 400
                              }}
                            >
                              {tag}
                            </Tag>
                          ))}
                        </Flex>
                        <div style={{ marginTop: 8 }}>
                          <Text
                            type="secondary"
                            style={{
                              marginBottom: 12,
                              display: "block",
                              fontSize: 14,
                              fontWeight: 400,
                              color: isLocked ? token.colorTextDisabled : token.colorTextSecondary,
                            }}
                          >
                            {isLocked ? "Complete previous modules to unlock" : module.description}
                          </Text>
                          <Flex gap={16} wrap="wrap">
                            <Flex align="center" gap={6}>
                              <Icon as={Clock} size={14} style={{ opacity: 0.5 }} />
                              <Text style={{ fontSize: 12, fontWeight: 400, opacity: 0.7 }}>
                                {module.totalSteps} {module.totalSteps === 1 ? "topic" : "topics"}
                              </Text>
                            </Flex>
                            <Flex align="center" gap={6}>
                              <Icon as={FileText} size={14} style={{ opacity: 0.5 }} />
                              <Text style={{ fontSize: 12, fontWeight: 400, opacity: 0.7 }}>
                                {module.totalQuizQuestions || 0} {module.totalQuizQuestions === 1 ? "quiz" : "quizzes"}
                              </Text>
                            </Flex>
                          </Flex>
                        </div>
                      </div>
                      <Flex align="center" gap={12}>
                        <Tag
                          bordered={false}
                          style={{
                            background: status === 'completed' ? alpha(token.colorText, 0.1) : status === 'in-progress' ? alpha(token.colorText, 0.1) : 'rgba(0,0,0,0.05)',
                            color: status === 'completed' ? token.colorText : status === 'in-progress' ? token.colorText : token.colorTextSecondary,
                            borderRadius: token.borderRadius / 4,
                            fontWeight: 400,
                            padding: "2px 8px"
                          }}
                        >
                          {getStatusLabel(status)}
                        </Tag>
                        {status !== "locked" ? (
                          <Icon as={CirclePlay} size={18} style={{ color: token.colorText }} />
                        ) : (
                          <Icon as={Lock} size={18} style={{ color: token.colorTextDisabled }} />
                        )}
                      </Flex>
                    </Flex>
                  </List.Item>
                  {!isLastItem && <Divider style={{ margin: 0 }} />}
                </React.Fragment>
              );
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default ModuleContainerCard;
